<template>
  <div class="frontpage">
    <Errorpage v-if="error" :msg="error" />
    <template v-if="!error && data">
      <div class="row">
        <div class="col-md-8 global-graphs">
          <h6>Certificate Breakdown by Type</h6>

          <div class="row">
            <Filters :breakdownFilters="breakdownFilters" :unselect="unselect" />
          </div>

          <div class="row">
            <div class="col-md-4">
              <PieGraph id="vallevel" :data="breakdown['valLevel']" :select="select('valLevel')" />
              <h6 class="sub-header">Validation Level</h6>
            </div>
            <div class="col-md-4">
              <PieGraph id="pubalg" :data="breakdown['pubAlg']" :select="select('pubAlg')" />
              <h6 class="sub-header">Public Key Algorithm</h6>
            </div>
            <div class="col-md-4">
              <PieGraph id="sigalg" :data="breakdown['sigAlg']" :select="select('sigAlg')" />
              <h6 class="sub-header">Signature Algorithm</h6>
            </div>
          </div>

          <div class="row">
            <div class="col-md-2">&nbsp;</div>
            <div class="col-md-4">
              <PieGraph id="entrytype" :data="breakdown['entryType']" :select="select('entryType')" />
              <h6 class="sub-header">Entry Type</h6>
            </div>
            <div class="col-md-4">
              <PieGraph id="expired" :data="breakdown['expired']" :select="select('expired')" />
              <h6 class="sub-header">Expired vs. Current</h6>
            </div>
            <div class="col-md-2">&nbsp;</div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <CompGraph id="root" :data="breakdown['rootName']" :select="select('rootName')" />
              <h6 class="sub-header">Root Certificate Authorities</h6>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <h6>Global Details</h6>

          <p>
            <span><u>Issuance Rate:</u> {{ data.Details.IssuanceRate.toLocaleString() }} certs/hr</span><br />
            <span><u>Expiry Rate:</u> {{ data.Details.ExpiryRate.toLocaleString() }} certs/hr</span><br />
          </p>

          <h6>Legacy Algorithms</h6>

          <p>The tables below count the number of currently-valid certificates
          signed by widely trusted CAs that use insecure or deprecated
          algorithms.</p>

          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th scope="col">Signature Algorithm</th>
                <th class="text-right" scope="col">#</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="alg in legacySigAlgs">
                <td>{{ alg.name }}</td><td class="text-right">{{ alg.count.toLocaleString() }}</td>
              </tr>
              <tr v-if="legacySigAlgs.length == 0">
                <td colspan="2"><em>(None)</em></td>
              </tr>
            </tbody>
          </table>

          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th scope="col">Public-Key Algorithm</th>
                <th class="text-right" scope="col">#</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="alg in legacyPubAlgs">
                <td>{{ alg.name }}</td><td class="text-right">{{ alg.count.toLocaleString() }}</td>
              </tr>
              <tr v-if="legacyPubAlgs.length == 0">
                <td colspan="2"><em>(None)</em></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <h6>Issuance per Day by Certificate Authority</h6>

          <IssuanceGraph id="global" :data="data.Issuance" />
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <h6>Log Utilization by Large Certificate Authorities</h6>

          <p>This section shows the number of pre-certificates in known CT logs
          from a few particularly large certificate authorities. Anyone can
          submit a pre-certificate they find in one log to another log, but this
          currently shows that some CAs have a clear preference for certain CT
          logs when they issue a certificate containing embedded SCTs, while
          others submit to several logs equally.</p>

          <UtilGraph :data="data.Utilization" />

        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import * as roll from '@/components/mixins/roll'
  import { loadData } from '@/components/mixins/util'

  import Filters from '@/components/widgets/Filters'
  import CompGraph from '@/components/widgets/CompGraph'
  import PieGraph from '@/components/widgets/PieGraph'
  import IssuanceGraph from '@/components/widgets/IssuanceGraph'
  import UtilGraph from '@/components/widgets/UtilGraph'
  import Errorpage from '@/components/widgets/Errorpage'

  export default {
    name: 'Frontpage',
    beforeRouteEnter: function(to, from, next) { loadData('/index', next) },
    // beforeRouteUpdate omitted because this page has no parameters.

    data: function() {
      return {
        error: null,

        data: null,
        breakdownFilters: {},
      }
    },
    computed: {
      breakdown: function() { return roll.parse(this.data.Breakdown, this.breakdownFilters) },
      legacySigAlgs: function() {
        let obj = this.data.Details.LegacySigAlgs, out = []
        for (let key in obj) {
          out.push({name: key, count: obj[key]})
        }
        return out.sort(function(a, b) { return a.count - b.count })
      },
      legacyPubAlgs: function() {
        let obj = this.data.Details.LegacyPubAlgs, out = []
        for (let key in obj) {
          out.push({name: key, count: obj[key]})
        }
        return out.sort(function(a, b) { return a.count - b.count })
      },
    },
    methods: {
      setData(err, data) {
        if (err) {
          this.error = err.toString()
        } else {
          this.data = data
        }
      },
      select: function(field) {
        let self = this
        return function(image) {
          let filters = Object.assign({}, self.breakdownFilters)
          filters[field] = image
          self.breakdownFilters = filters
        }
      },
      unselect: function(field) {
        let filters = Object.assign({}, this.breakdownFilters)
        delete filters[field]
        this.breakdownFilters = filters
      },
    },
    components: { Filters, CompGraph, PieGraph, IssuanceGraph, UtilGraph, Errorpage },
  }
</script>

<style scoped>
  h6.sub-header {
    padding: 0.1rem;
    margin: 0;
    border-bottom: none;
    text-align: center;
  }
  div.row {
    margin-bottom: 1rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/Frontpage.vue